/**
 * Derived views: the four non-`kv` sheet tabs, and the body-composition arithmetic the
 * Progress screen needs.
 *
 * These are computed from `kv` and plan content. If a derived view and `kv` ever
 * disagree, `kv` is right — which is why the Apps Script replaces these tabs wholesale
 * rather than upserting them.
 */
import { PLAN, META } from './data.ts';
import type { PlanState } from './types.ts';
import type { KvRow } from '../data/engine.ts';
import { dateForDayIndex, MAX_DAY_INDEX } from './dates.ts';
import { weighIns, waistEntries, MIN_WEIGH_INS_FOR_MEAN } from './weights.ts';
import { effectiveSessionId } from './reschedule.ts';
import { dayPlan, eatenSoFar, slotsFor, mealsTicked } from './nutrition.ts';
import { sessionProgress } from './progress.ts';

const ROLLING_WINDOW_DAYS = 7;

/** Mean of the weigh-ins in the seven calendar days ending on `dateIso`. Null when
 *  there are too few to be a real mean, matching the app's own standard. */
export function trailingMeanOn(
  entries: Array<{ date: string; kg: number }>, dateIso: string,
): number | null {
  const end = new Date(`${dateIso}T00:00:00`);
  const start = new Date(end);
  start.setDate(start.getDate() - (ROLLING_WINDOW_DAYS - 1));
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  const window = entries.filter((e) => e.date >= iso(start) && e.date <= dateIso);
  if (window.length < MIN_WEIGH_INS_FOR_MEAN) return null;
  return window.reduce((s, e) => s + e.kg, 0) / window.length;
}

export interface BodyComp {
  weightKg: number; bodyFatPct: number;
  fatKg: number; leanKg: number;
}

export const START_COMP: BodyComp = comp(
  PLAN.meta.subject.startWeightKg as number, PLAN.meta.subject.startBodyFatPct as number);
export const TARGET_COMP: BodyComp = comp(
  PLAN.meta.subject.targetWeightKg as number, PLAN.meta.subject.targetBodyFatPct as number);

export function comp(weightKg: number, bodyFatPct: number): BodyComp {
  const fatKg = weightKg * (bodyFatPct / 100);
  return { weightKg, bodyFatPct, fatKg, leanKg: weightKg - fatKg };
}

/** Latest body composition, or null when nothing usable has been logged. Deliberately
 *  returns null rather than guessing: an invented body-fat figure would make the
 *  fat-lost and lean-held meters lie. */
export function latestComp(state: PlanState): BodyComp | null {
  const withBf = weighIns(state).filter((w) => typeof w.bf === 'number' && Number.isFinite(w.bf));
  if (withBf.length === 0) return null;
  const last = withBf[withBf.length - 1];
  return comp(last.kg, last.bf as number);
}

// ------------------------------------------------------------------ sheet views

export type ViewRows = Record<string, unknown[][]>;

/** `weights`: date, kg, body_fat_pct, trailing_7day_mean */
export function weightsView(state: PlanState): unknown[][] {
  const entries = weighIns(state);
  return entries.map((w) => {
    const mean = trailingMeanOn(entries, w.date);
    return [w.date, w.kg, w.bf ?? '', mean === null ? '' : round(mean, 2)];
  });
}

/** `waist`: date, cm, change_from_baseline. Baseline is the first measurement. */
export function waistView(state: PlanState): unknown[][] {
  const entries = waistEntries(state);
  if (entries.length === 0) return [];
  const baseline = entries[0].cm;
  return entries.map((w) => [w.date, w.cm, round(w.cm - baseline, 1)]);
}

/** `loads`: phase, session, exercise, weight_kg, updated_at. Needs raw rows for the
 *  timestamp, which the plan snapshot does not carry. */
export function loadsView(rows: KvRow[]): unknown[][] {
  return rows
    .filter((r) => !r.deleted && r.k.startsWith('load:'))
    .map((r) => {
      const [, phase, session, exIndex] = r.k.split(':');
      const name = exerciseName(phase, session, Number(exIndex));
      return [Number(phase), session, name, JSON.parse(r.v), r.updated_at];
    })
    .sort((a, b) => Number(a[0]) - Number(b[0])
      || String(a[1]).localeCompare(String(b[1]))
      || String(a[2]).localeCompare(String(b[2])));
}

function exerciseName(phase: string, session: string, exIndex: number): string {
  const block = PLAN.lifts[phase];
  if (!block) return `(unknown phase ${phase})`;
  const s = session === 'liftA' ? block.A : session === 'liftB' ? block.B : null;
  return s?.ex[exIndex]?.n ?? `(unknown exercise ${exIndex})`;
}

const DOW_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * `daily`: one row per day of the plan.
 * date, week, day_of_week, session_type, sets_done, sets_total, meals_ticked,
 * meals_total, kcal_planned, kcal_ticked, protein_planned, protein_ticked,
 * water, steps, vitamin_d
 */
export function dailyView(state: PlanState, startDate: string): unknown[][] {
  const out: unknown[][] = [];
  for (let d = 0; d <= MAX_DAY_INDEX; d++) {
    const week = Math.floor(d / 7) + 1;
    const dow = d % 7;
    const id = effectiveSessionId(state, d);
    const sets = id === 'rest'
      ? { done: 0, total: 0 }
      : sessionProgress(state, startDate, week, d, id);
    const plan = dayPlan(state, d);
    const eaten = eatenSoFar(state, d);
    const slots = slotsFor(state, d);
    out.push([
      dateForDayIndex(startDate, d), week, DOW_SHORT[dow], id,
      sets.done, sets.total,
      mealsTicked(state, d), slots.length,
      round(plan.k, 0), round(eaten.k, 0),
      round(plan.p, 1), round(eaten.p, 1),
      state.get(`habit:water:${d}`) === 1 ? 1 : 0,
      state.get(`habit:steps:${d}`) === 1 ? 1 : 0,
      state.get(`habit:vitd:${d}`) === 1 ? 1 : 0,
    ]);
  }
  return out;
}

export function buildViews(state: PlanState, rows: KvRow[], startDate: string): ViewRows {
  return {
    weights: weightsView(state),
    waist: waistView(state),
    loads: loadsView(rows),
    daily: dailyView(state, startDate),
  };
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/** Week-by-week trajectory reference for the chart and the Week screen. */
export function trajectoryFor(week: number): { weightKg: number; bodyFatPct: number } {
  const i = Math.min(Math.max(week, 0), 16);
  return { weightKg: PLAN.trajectory.weightKg[i], bodyFatPct: PLAN.trajectory.bodyFatPct[i] };
}

export { META };
